import { z } from 'zod/v4';

import { bidSizeRangesSchema } from '../shared/bid-size-schema';

// The contractor report renders EVERY active owner (an owner the contractor has
// won from — `HAVING SUM(won) > 0`), so the per-owner sweep is not capped to a
// top-N by default. `topOwners` defaults to a high ceiling that covers any real
// contractor's owner set; a caller can still pass a smaller value (e.g. a quick
// preview) but the reconciliation invariant (company == Σ owners) only holds
// when every active owner is included, which the default guarantees.
const DEFAULT_TOP_OWNERS = 200;
const MAX_TOP_OWNERS = 200;

// The contractor report profiles up to 10 global vendors at once, assembling a
// per-contractor → per-owner, cross-state rollup. Unlike the targeted-state
// report there is NO state axis — the footprint and owner ranking span every
// state the contractor is active in.
export const contractorReportQuerySchema = z.object({
  // The contractors to profile (global vendor ids) — at least one, capped at 10.
  vendorIds: z.array(z.string().uuid()).min(1).max(10),
  // The effective letting-date window. When omitted, the service defaults to the
  // in-app All-Time default (last 5 years … today) and stamps it on every slide.
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
  // Bounds every section's metrics / owner ranking by bid amount (omit = all).
  bidSizes: bidSizeRangesSchema,
  // How many active owners to surface per contractor (default = all of them).
  topOwners: z
    .number()
    .int()
    .min(1)
    .max(MAX_TOP_OWNERS)
    .default(DEFAULT_TOP_OWNERS),
});

export type ContractorReportQuery = z.infer<typeof contractorReportQuerySchema>;
